import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {ProgressBar} from 'primereact/progressbar';
import {MapContainer, TileLayer, FeatureGroup, Polygon, useMap} from 'react-leaflet';
import {EditControl} from 'react-leaflet-draw';
import {FileUpload} from 'primereact/fileupload';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

const LocationGeometryFormComponent = ({locationId, item, t, onCancel, onSuccess}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [geoJson, setGeoJson] = useState(null);
  const featureGroupRef = useRef(null);
  const fileUploadRef = useRef(null);
  const defaultCenter = [47.4979, 19.0402];

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  };

  useEffect(() => {
    if (item) {
      setName(item.name || '');
      setGeoJson(item.geoJson || item.geo_json || null);
    } else {
      setName('');
      setGeoJson(null);
    }
  }, [item]);

  const normalizeGeoJson = (json) => {
    let parsedGeoJson = json;
    if (json.type === 'Feature') {
      parsedGeoJson = json.geometry;
    }
    if (json.type === 'FeatureCollection') {
      const polygonFeatures = json.features.filter(feature =>
        feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'MultiPolygon'
      );
      if (!polygonFeatures.length) {
        throw new Error('A fájl nem tartalmaz Polygon vagy MultiPolygon geometriát.');
      }
      const coordinates = [];
      polygonFeatures.forEach(feature => {
        if (feature.geometry.type === 'Polygon') {
          coordinates.push(feature.geometry.coordinates);
        }
        if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach(polygon => {
            coordinates.push(polygon);
          });
        }
      });
      return {
        type: 'MultiPolygon',
        coordinates
      };
    }
    if (parsedGeoJson.type === 'Polygon') {
      return {
        type: 'MultiPolygon',
        coordinates: [
          parsedGeoJson.coordinates
        ]
      };
    }
    if (parsedGeoJson.type === 'MultiPolygon') {
      return parsedGeoJson;
    }
    throw new Error('Csak Polygon vagy MultiPolygon típusú GeoJSON használható.');
  };

  const onJsonUpload = (event) => {
    const file = event.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        const parsedGeoJson = normalizeGeoJson(json);
        setGeoJson(parsedGeoJson);
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
        window.App.toastShow('success', 'GeoJSON fájl betöltve!');
      } catch (error) {
        window.App.toastShow('error', error.message || 'Hibás JSON fájl!');
      }
    };

    reader.readAsText(file);
  };

  const getPolygonsFromGeoJson = () => {
    if (!geoJson?.coordinates) {
      return [];
    }
    return geoJson.coordinates.map((polygon) => {
      return polygon[0].map(([lng, lat]) => [lat, lng]);
    });
  };

  const layerToCoordinates = (layer) => {
    const latLngs = layer.getLatLngs();
    if (!latLngs || !latLngs.length) {
      return null;
    }
    const outerRing = latLngs[0].map(point => [
      point.lng,
      point.lat
    ]);
    if (outerRing.length > 0) {
      const first = outerRing[0];
      const last = outerRing[outerRing.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        outerRing.push(first);
      }
    }
    return [
      outerRing
    ];
  };

  const updateGeoJsonFromLayers = () => {
    const featureGroup = featureGroupRef.current;
    if (!featureGroup) {
      return;
    }
    const coordinates = [];
    featureGroup.eachLayer(layer => {
      if (typeof layer.getLatLngs === 'function') {
        const polygonCoordinates = layerToCoordinates(layer);

        if (polygonCoordinates) {
          coordinates.push(polygonCoordinates);
        }
      }
    });
    if (!coordinates.length) {
      setGeoJson(null);
      return;
    }
    setGeoJson({
      type: 'MultiPolygon',
      coordinates
    });
  };

  const saveGeometry = () => {
    if (!name) {
      window.App.toastShow('error', 'A név megadása kötelező!');
      return;
    }
    if (!geoJson) {
      window.App.toastShow('error', 'Terület geometria megadása kötelező!');
      return;
    }
    const payload = {
      location: Number(locationId),
      name,
      geoJson
    };
    setLoading(true);

    const request = item
      ? apiCall('patch', '/api/v2/location-geometry/' + item.id, payload)
      : apiCall('post', '/api/v2/location-geometry', payload);

    request
      .then(() => {
        window.App.toastShow('success', 'Sikeres mentés!');
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const FitBounds = ({geoJson}) => {
    const map = useMap();
    useEffect(() => {
      if (!geoJson?.coordinates?.length) {
        return;
      }
      const allPoints = [];
      geoJson.coordinates.forEach(polygon => {
        polygon[0].forEach(([lng, lat]) => {
          allPoints.push([lat, lng]);
        });
      });
      if (allPoints.length) {
        map.fitBounds(allPoints, {
          padding: [30, 30]
        });
      }
    }, [geoJson, map]);
    return null;
  };

  return (
    <div className="location-geometry-form">
      {loading &&
        <ProgressBar
          mode="indeterminate"
          style={{height: '5px', borderRadius: 0}}
        />
      }
      <div className="p-grid">
        <div className="p-col-12">
          <div className="p-field p-fluid">
            <label htmlFor="geometry_name">{t('fieldName')}</label>
            <InputText
              id="geometry_name"
              value={name || ''}
              onChange={(e) => setName(e.target.value)}
              disabled={!localStorage.getItem('roles').toString().includes('role_location_geometry_write')}
            />
          </div>
        </div>
        <div className="p-col-12 p-mb-2">
          <label className="p-d-block p-mb-2">{t('upload')}</label>
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            name="geoJson"
            accept=".json,.geojson,application/json"
            maxFileSize={10000000}
            customUpload
            auto
            chooseLabel={t('chooseFile')}
            uploadHandler={onJsonUpload}
            disabled={!localStorage.getItem('roles').toString().includes('role_location_geometry_write')}
          />
        </div>
        <div className="p-col-12">
          <MapContainer
            center={defaultCenter}
            zoom={13}
            style={{height: '500px', width: '100%'}}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FeatureGroup ref={featureGroupRef}>
              {localStorage.getItem('roles').toString().includes('role_location_geometry_write') &&
                <EditControl
                  position="topright"
                  onCreated={updateGeoJsonFromLayers}
                  onEdited={updateGeoJsonFromLayers}
                  onDeleted={updateGeoJsonFromLayers}
                  draw={{
                    rectangle: false,
                    circle: false,
                    circlemarker: false,
                    marker: false,
                    polyline: false,
                    polygon: true
                  }}
                  edit={{
                    edit: true,
                    remove: true
                  }}
                />
              }
              {getPolygonsFromGeoJson().map((positions, index) => (
                <Polygon
                  key={index}
                  positions={positions}
                />
              ))}
            </FeatureGroup>
            <FitBounds geoJson={geoJson} />
          </MapContainer>
        </div>
        {/* <div className="p-col-12">
          <pre style={{maxHeight: '200px', overflow: 'auto'}}>
            {geoJson ? JSON.stringify(geoJson, null, 2) : 'Nincs geometria megadva'}
          </pre>
        </div> */}
        <div className="p-col-12 p-text-right">
          <Button
            label={t('cancel')}
            icon="pi pi-times"
            className="p-button"
            onClick={onCancel}
          />
          <Button
            label={t('save')}
            icon="pi pi-save"
            className="p-button-success p-ml-2"
            onClick={saveGeometry}
            loading={loading}
            disabled={!localStorage.getItem('roles').toString().includes('role_location_geometry_write')}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationGeometryFormComponent;